import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { ENDPOINTS } from '@/util'
import { useSuspenseQuery } from '@tanstack/react-query'
import { PageResponse, PageUiSchema } from './types'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { useLocation } from 'react-router'
import { useSessionStorage } from '@/hooks'
import { getFilterSelectionsSignature, getMatchingFilters } from './utils'
import { useMemo } from 'react'
import { isValidCatalogPageId } from '../component-library/MdTextComponent/mdTextViewerNavigation'

const USE_MOCK_DATA = false

const EMPTY_BOOTSTRAP_PAGE: PageUiSchema = {
  id: '',
  roles: [],
  pageFilters: [],
  containers: [],
}

const defaultContainer = {
  id: 5,
  position: {
    colStart: 1,
    rowStart: 1,
    colSpan: 1,
    rowSpan: 2,
  },
  component: {
    type: 'quick-nav',
    id: '',
  },
}
interface GetAppPageParams {
  appKey?: string
  pageId?: string
  projectId?: number
  isPageBuilder?: boolean
  filterSelections?: Record<string, unknown>[]
}
const getAppPage = async ({ appKey, pageId, projectId, isPageBuilder, filterSelections = [] }: GetAppPageParams) => {
  if (!appKey || !pageId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder) {
    return (await import('@/catalog-apps/mock-data/page-layout/page-builder-default-layout.json')).json
      .uiSchema as PageUiSchema
  }
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  if (USE_MOCK_DATA) {
    return Promise.resolve(
      (await import('@/catalog-apps/mock-data/page-layout/page-layout-single-chart.json')).json.uiSchema as PageUiSchema
    )
  }

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: pageId,
      project_id: projectId,
      filters: filterSelections,
    }),
  })
  const data = await response.json()
  if (response.ok) {
    if (!data.uiSchema.containers.length) {
      const withDefaultContainer = {
        ...data,
        uiSchema: {
          ...data.uiSchema,
          containers: [defaultContainer],
        },
      }
      return (withDefaultContainer as PageResponse).uiSchema
    } else {
      return (data as PageResponse).uiSchema
    }
  }
  throw new Error('Failed to fetch app page')
}

function useAppPageParams() {
  const { pathname } = useLocation()
  const isPageBuilderCondition = useIsPageBuilder()
  const isPageBuilder = isPageBuilderCondition || (pathname.includes('/applications/') && !pathname.includes('/apps/'))
  const { pageId, appKey, projectId, pageFilterStorageKey } = useAppContext()

  return { pageId, appKey, projectId, pageFilterStorageKey, isPageBuilder }
}

export function useGetBootstrapAppPage() {
  const { pageId, appKey, projectId, isPageBuilder } = useAppPageParams()
  const canFetchPage = isValidCatalogPageId(pageId)

  return useSuspenseQuery({
    queryKey: ['app-page-bootstrap', pageId, appKey, projectId, isPageBuilder, canFetchPage],
    queryFn: () => {
      if (!canFetchPage) {
        return Promise.resolve(EMPTY_BOOTSTRAP_PAGE)
      }
      return getAppPage({ appKey, pageId, projectId, isPageBuilder, filterSelections: [] })
    },
    // 24 hour stale time
    staleTime: 1000 * 60 * 60 * 24,
  })
}

export function useGetAppPage() {
  const { pageId, appKey, projectId, pageFilterStorageKey, isPageBuilder } = useAppPageParams()
  const { data: bootstrapPage } = useGetBootstrapAppPage()
  const [filterSelectionsPersisted] = useSessionStorage<Record<string, unknown>[]>(pageFilterStorageKey, [])
  const filterSelections = useMemo(
    () => getMatchingFilters(filterSelectionsPersisted, bootstrapPage.pageFilters),
    [filterSelectionsPersisted, bootstrapPage.pageFilters]
  )
  const filterSignature = useMemo(() => getFilterSelectionsSignature(filterSelections), [filterSelections])

  const canFetchPage = isValidCatalogPageId(pageId)

  return useSuspenseQuery({
    queryKey: ['app-page-filtered', pageId, appKey, projectId, isPageBuilder, filterSignature, canFetchPage],
    queryFn: () => {
      if (!canFetchPage) {
        return Promise.resolve(EMPTY_BOOTSTRAP_PAGE)
      }
      return getAppPage({ appKey, pageId, projectId, isPageBuilder, filterSelections })
    },
    staleTime: 1000 * 60 * 60 * 24,
  })
}
